import { useNavigate, useParams } from "react-router-dom";
import { useInsightsCtx } from "./InsightsLayout";
import { useSensorReport } from "./hooks/useSensorReport";
import { currentTenureIsOwned } from "../../services/installationTenure";
import {
  SECTION_ACTIVITY,
  SECTION_ALERTS,
  SECTION_EQUIPMENT,
  changeText,
  hoursText,
  type UnitReport,
} from "./buildingReport";
import Spinner from "../../components/ui/Spinner";
import ErrorState from "../../components/ui/ErrorState";

/**
 * One sensor's weekly report.
 *
 * The same document as the property report, narrowed to a single kitchen: same
 * window, same counting, same wording, so a number here and the row this unit
 * occupies on its property's report are the same number by construction.
 *
 * Says nothing about presence. That is the wellness question, asked of a
 * different reader by a different feature, and this page must not appear to
 * answer it.
 */

const TONES: Record<string, string> = {
  shutoffs: "text-alert-critical",
  alerts: "text-alert-warning",
  offline: "text-status-offline",
};

function plural(count: number, singular: string, other?: string) {
  return count === 1 ? singular : other || `${singular}s`;
}

/** Downtime, or an honest "unknown". */
function offlineCell(unit: UnitReport): string {
  if (unit.offlineHours === null) return "unknown";
  if (unit.offlineHours === 0) return "none";
  return hoursText(unit.offlineHours);
}

function Kpi({ value, label }: { value: number | string; label: string }) {
  return (
    <div className="px-5 py-4">
      <div className="text-3xl font-bold text-ink-primary dark:text-white">
        {value}
      </div>
      <div className="text-xs text-ink-tertiary mt-1">{label}</div>
    </div>
  );
}

export default function SensorReportPage() {
  const { buildingId = "", sensorId = "" } = useParams();
  const navigate = useNavigate();
  const { buildings, buildingsLoading } = useInsightsCtx();

  const building = buildings.find(
    (b: { buildingId: string }) => b.buildingId === buildingId
  );

  const { report, status, tenure } = useSensorReport(sensorId, "");
  // Whether the cutoff these figures rest on is one this organization can
  // claim. See currentTenureIsOwned: a "known" cutoff can still be someone
  // else's, and the report must say so rather than imply otherwise.
  const currentTenureOwned = currentTenureIsOwned(
    tenure,
    buildings.map((b: { name: string }) => b.name).filter(Boolean)
  );

  if (buildingsLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size={96} />
      </div>
    );
  }

  if (!building) {
    return (
      <ErrorState
        title="Property not found"
        message="We couldn't find a property matching this URL."
        onRetry={() => navigate("/insights")}
      />
    );
  }

  if (status === "error") {
    return (
      <ErrorState
        title="Report unavailable"
        message="We couldn't load this sensor's report. Please try again."
        onRetry={() => navigate(0)}
      />
    );
  }

  if (status === "loading" || !report) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size={96} />
      </div>
    );
  }

  const { unit, issues, sections } = report;
  const showActivity = sections.includes(SECTION_ACTIVITY);
  const showAlerts = sections.includes(SECTION_ALERTS);
  const showEquipment = sections.includes(SECTION_EQUIPMENT);

  const comparison: [string, number, number, number][] = [];
  if (unit.previous && unit.delta) {
    if (showActivity) {
      comparison.push([
        "Cooking sessions",
        unit.sessions,
        unit.previous.sessions,
        unit.delta.sessions,
      ]);
    }
    if (showAlerts) {
      comparison.push(
        ["Alerts", unit.alerts, unit.previous.alerts, unit.delta.alerts],
        [
          "Safety shut-offs",
          unit.shutoffs,
          unit.previous.shutoffs,
          unit.delta.shutoffs,
        ]
      );
    }
  }

  return (
    <div className="print-root mx-auto max-w-3xl px-4 py-6">
      <div className="no-print mb-4 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() =>
            navigate(
              `/insights/properties/${buildingId}/sensors/${encodeURIComponent(
                sensorId
              )}`
            )
          }
          className="text-sm text-ink-tertiary hover:text-ink-primary dark:hover:text-white"
        >
          Back to sensor
        </button>
        <button
          type="button"
          onClick={() => window.print()}
          className="rounded bg-primary px-4 py-2 text-sm font-semibold text-black"
        >
          Print or save PDF
        </button>
      </div>

      <header className="mb-6">
        <h1 className="text-xl font-bold text-ink-primary dark:text-white">
          {report.name}
        </h1>
        <p className="text-sm text-ink-tertiary">
          Weekly report, {report.periodLabel}. {building.name}.
        </p>
      </header>

      {/* How the figures are scoped. Said before the numbers, because it changes
          what they mean: two different sentences, since a failed read is not a
          known absence of a record. */}
      {tenure.reason !== "known" && (
        <p
          className={`mb-4 text-sm italic ${
            tenure.reason === "fetch-failed"
              ? "text-alert-warning"
              : "text-ink-tertiary"
          }`}
        >
          {tenure.reason === "fetch-failed"
            ? "We couldn't load this sensor's install history, so these figures are not scoped to its current location and may span more than one."
            : "There is no installation record for this sensor, so these figures cover wherever it has been rather than only its current location."}
        </p>
      )}

      {/* A known cutoff that is not OURS. A sensor transferred between
          organizations by reassigning its group writes no installation row, so
          the newest one on file can be the previous owner's and this whole
          report would describe their tenure. */}
      {tenure.reason === "known" && !currentTenureOwned && (
        <p className="mb-4 text-sm italic text-alert-warning">
          This sensor's most recent installation is at a property we can't match
          to your organization, so this report may cover a period before you
          owned it.
        </p>
      )}

      {tenure.reason === "known" && unit.previous === null && (
        <p className="mb-4 text-sm italic text-ink-tertiary">
          This sensor was installed at its current location during or after the
          comparison period, so there is no previous week to compare against here.
        </p>
      )}

      {showEquipment && unit.offlineHours === null && (
        <p className="mb-4 text-sm italic text-alert-warning">
          This sensor had no connection record for the period -- or none from
          before its current install -- so its offline time is shown as unknown
          rather than as none.
        </p>
      )}

      <div className="mb-8 flex flex-wrap divide-x divide-ink-tertiary/20 rounded border border-ink-tertiary/20">
        {showAlerts && (
          <>
            <Kpi
              value={unit.shutoffs}
              label={plural(unit.shutoffs, "safety shut-off")}
            />
            <Kpi value={unit.alerts} label={plural(unit.alerts, "alert")} />
          </>
        )}
        {showActivity && (
          <Kpi
            value={unit.sessions}
            label={plural(unit.sessions, "cooking session")}
          />
        )}
        {showEquipment && <Kpi value={offlineCell(unit)} label="offline" />}
      </div>

      <section className="mb-8">
        <h2 className="mb-2 text-base font-semibold text-ink-primary dark:text-white">
          Needs attention
        </h2>
        {issues.length > 0 ? (
          <ul className="space-y-1 text-sm">
            {issues.map((issue) => (
              <li key={issue.kind} className={TONES[issue.kind] || ""}>
                {issue.text}
              </li>
            ))}
          </ul>
        ) : (
          // Printed, not omitted. On a safety product the all-clear is the
          // point of the report, not the absence of content.
          <p className="text-sm text-ink-tertiary">
            Nothing needed attention this period.
          </p>
        )}
      </section>

      {comparison.length > 0 && (
        <section className="mb-8">
          {/*
            Never labelled "trend". That belongs to the 28 day graded badge on
            the sensor page, and two different measures wearing one name is how
            a reader stops trusting both.
          */}
          <h2 className="mb-2 text-base font-semibold text-ink-primary dark:text-white">
            Compared with the previous 7 days
          </h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-ink-tertiary/40 text-left">
                <th className="py-2 pr-3 font-semibold"> </th>
                <th className="py-2 pr-3 text-right font-semibold">
                  This period
                </th>
                <th className="py-2 pr-3 text-right font-semibold">Previous</th>
                <th className="py-2 text-right font-semibold">Change</th>
              </tr>
            </thead>
            <tbody>
              {comparison.map(([label, now, before, delta]) => (
                <tr key={label} className="border-b border-ink-tertiary/20">
                  <td className="py-2 pr-3">{label}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{now}</td>
                  <td className="py-2 pr-3 text-right tabular-nums text-ink-tertiary">
                    {before}
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    {changeText(delta)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <p className="text-sm text-ink-tertiary">
        Covers stove activity only. It cannot tell who cooked or whether anyone
        ate.
      </p>
    </div>
  );
}
